import React, {
  Dispatch,
  FC,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import {Log} from '@aboutyou/mlm';

const PageStateContext = createContext({
  store: {} as Store,
  getPageStateForKey: <S extends unknown>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _stateKey: string,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _initialState: S,
  ): [S, Dispatch<SetStateAction<S>>] => {
    throw new Error(
      `PageStateContext.getPageStateForKey default function was called, which is not supported. Did you miss to inject a PageStateProvider as a parent of this component?`,
    );
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ensureUniqueInstance: (
    _uniqueKey: string,
    _uniqueSymbol: symbol,
    _silenceErrors: boolean,
  ): void => {
    throw new Error(
      `PageStateContext.ensureUniqueInstance default function was called, which is not supported. Did you miss to inject a PageStateProvider as a parent of this component?`,
    );
  },
});

interface StoreItem {
  [key: string]: unknown;
}
interface Store {
  [pathname: string]: StoreItem;
}

interface Action {
  pageKey: string;
  stateKey: string;
  value: unknown;
  mutate?: boolean;
}

function storeReducer(state: Store, action: Action) {
  const {pageKey, stateKey, value, mutate = false} = action;
  if (mutate) {
    if (!state[pageKey]) {
      state[pageKey] = {};
    }
    state[pageKey]![stateKey] = value;
    return state;
  }
  return {
    ...state,
    [pageKey]: {
      ...(state[pageKey] || {}),
      [stateKey]: value,
    },
  };
}

interface PageStateProviderProps {
  /**
   * Based on the `pageKey` the internal state is toggled. Most router implementation expose a value like this. For a poor man's approach `location.href` might work as well.
   *
   * @type {(string | undefined)}
   * @memberof PageStateProviderProps
   */
  pageKey: string | undefined;
}

const DEFAULT_KEY = '__LANDINGPAGE__';
export const PageStateProvider: FC<
  React.PropsWithChildren<PageStateProviderProps>
> = props => {
  const {children, pageKey = DEFAULT_KEY} = props;
  const [store, dispatch] = useReducer(storeReducer, {} as Store);
  const initialStateStore = useRef({} as Store);
  const [uniqueSymbolMap] = useState(
    () => new Map<string, Map<string, symbol>>(),
  );
  const currentPageState = store?.[pageKey];

  const getPageStateForKey = useCallback(
    <S extends unknown>(stateKey: string, initialState: S) => {
      const currentState = currentPageState?.[stateKey];
      const exists = Object.prototype.hasOwnProperty.call(
        currentPageState ?? {},
        stateKey,
      );
      if (!initialStateStore.current[pageKey]) {
        initialStateStore.current[pageKey] = {};
      }
      if (!initialStateStore.current[pageKey][stateKey]) {
        initialStateStore.current[pageKey][stateKey] = initialState;
      }
      const realInitialState = initialStateStore.current[pageKey][stateKey];

      const update = (value: S) => {
        if (!exists && realInitialState === value) {
          return;
        }
        if (exists && currentState === value) {
          return;
        }
        dispatch({
          pageKey,
          stateKey,
          value,
        });
      };
      const state = currentState ?? realInitialState;
      return [state, update] as [S, Dispatch<SetStateAction<S>>];
    },
    [pageKey, currentPageState],
  );

  useEffect(
    () => () => {
      uniqueSymbolMap.delete(pageKey);
    },
    [pageKey, uniqueSymbolMap],
  );

  const ensureUniqueInstance = useCallback(
    (uniqueKey: string, uniqueIdentifier: symbol, silenceErrors: boolean) => {
      const cleanUp = () => {
        const page = uniqueSymbolMap.get(pageKey);
        page?.delete(uniqueKey);
      };
      let page = uniqueSymbolMap.get(pageKey);
      if (!page) {
        page = new Map();
        uniqueSymbolMap.set(pageKey, page);
      }

      const identifier = page.get(uniqueKey);
      if (!identifier) {
        page.set(uniqueKey, uniqueIdentifier);
        return cleanUp;
      }

      if (identifier !== uniqueIdentifier && !silenceErrors) {
        Log.warn(
          '@aboutyou/history-page-state',
          `Observed multiple useHistoryPageState components using the same unique key with name \`${uniqueKey}\` on one page. This results in shared state and can result in unwanted side effects.`,
        );
      }
      return cleanUp;
    },
    [uniqueSymbolMap, pageKey],
  );

  const ctx = useMemo(
    () => ({store, getPageStateForKey, ensureUniqueInstance}),
    [store, getPageStateForKey, ensureUniqueInstance],
  );

  return (
    <PageStateContext.Provider value={ctx}>
      {children}
    </PageStateContext.Provider>
  );
};

interface UseHistoryPageStateOptions {
  /**
   *The unique key is used to restore the state after a component was unmounted. If a key is used twice, it will end up in shared state which can cause unwanted side effects.
   *
   * @type {string}
   * @memberof UseHistoryPageStateOptions
   */
  uniqueKey: string;
  silenceErrors?: boolean;
}

/**
 * Returns a stateful value, and a function to update it comparable to `React.useState`.
 * It distinguish from `React.useState` that it resets and restores its value based on the route the user is on.
 * Common use cases are the state of loaded pages in an endless scrolling container, a selected active element in a tab module or the position of a slider.
 * This hook keeps the state even if the component is unmounted which might increase the memory footprint. Therefore it is recommended to not use it as a complete replacement for `React.useState` but only in situations where state should be persisted between pages.
 *
 * @param initialState
 * @param options
 */
export const useHistoryPageState = <T extends unknown>(
  initialState: T,
  options: UseHistoryPageStateOptions,
) => {
  const {getPageStateForKey, ensureUniqueInstance} =
    useContext(PageStateContext);
  const uniqueIdentifier = useRef(Symbol('UNIQUE_HISTORY_PAGE_STATE_SYMBOL'));
  const [currentState, updateCurrentState] = getPageStateForKey(
    options.uniqueKey,
    initialState,
  );

  useEffect(() => {
    ensureUniqueInstance(
      options.uniqueKey,
      uniqueIdentifier.current,
      options.silenceErrors ?? false,
    );
  }, [options.uniqueKey, uniqueIdentifier.current, options.silenceErrors]);
  return [currentState, updateCurrentState] as const;
};
